/**
 * Hook useCrmCustomersPaginated - Paginação server-side para listagem de clientes
 * Utiliza RPC get_customers_paginated com offset-based pagination
 * 
 * Estrutura replicada de useCrmDealsPaginated para consistência
 * 
 * @version 1.0.0
 * @author Sistema
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { CustomerType, CustomerLifecycleStage } from '@/hooks/useCrmCustomers';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('CrmCustomersPaginated');

/**
 * Interface do cliente retornado pela RPC get_customers_paginated
 * Estrutura flat otimizada para tabelas (sem embeds aninhados)
 */
export interface PaginatedCustomer {
  // Dados do cliente
  id: string;
  customer_type: CustomerType;
  nome: string | null;
  cpf: string | null;
  data_nascimento: string | null;
  razao_social: string | null;
  nome_fantasia: string | null;
  cnpj: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  lifecycle_stage: CustomerLifecycleStage;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Contato principal (flat)
  main_contact_id: string | null;
  main_contact_name: string | null;
  main_contact_phone: string | null;
  main_contact_email: string | null;
  main_contact_avatar: string | null;
  // Vendedor (flat)
  seller_id: string | null;
  seller_name: string | null;
  seller_email: string | null;
  seller_phone: string | null;
  seller_avatar: string | null;
  // v1.0.3: Colunas mascaradas (sigilo de dados — server-side)
  cpf_masked: string | null;
  cnpj_masked: string | null;
  main_contact_phone_masked: string | null;
  main_contact_email_masked: string | null;
  // Paginação (window function)
  total_count: number;
}

/**
 * Interface de filtros para listagem de clientes
 */
export interface CustomerPaginatedFilters {
  customer_type?: CustomerType | null;
  lifecycle_stage?: CustomerLifecycleStage | null;
  seller_id?: string | null;
  search?: string | null;
}

/**
 * Parâmetros de paginação
 */
interface PaginationParams {
  page: number;
  pageSize: number;
}

/**
 * Resultado da query paginada
 */
export interface PaginatedCustomersResult {
  customers: PaginatedCustomer[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook para buscar clientes com paginação server-side
 * Utiliza RPC get_customers_paginated para performance em grande escala
 * 
 * @param filters - Filtros para a busca (customer_type, lifecycle_stage, etc.)
 * @param pagination - Parâmetros de paginação (page, pageSize)
 */
export function useCrmCustomersPaginated(
  filters: CustomerPaginatedFilters | undefined,
  pagination: PaginationParams
): PaginatedCustomersResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    // Query key inclui filtros e paginação para cache correto
    queryKey: ['crm-customers-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando clientes paginados:', { filters, pagination });

      // Chama RPC com todos os parâmetros
      // NOTA: Casting necessário até types.ts ser regenerado
      const { data, error } = await (supabase.rpc as any)('get_customers_paginated', {
        p_page: pagination.page,
        p_page_size: pagination.pageSize,
        p_tenant_hint: currentTenant?.id || null,
        // Filtros
        p_customer_type: filters?.customer_type || null,
        p_lifecycle_stage: filters?.lifecycle_stage || null,
        p_seller_id: filters?.seller_id || null,
        p_search: filters?.search || null,
      });

      if (error) {
        logger.error('Erro ao buscar clientes paginados:', error);
        throw error;
      }

      // Total count vem em cada row (pattern COUNT(*) OVER())
      const rawCustomers = (data || []) as PaginatedCustomer[];
      const totalCount = rawCustomers[0]?.total_count ?? 0;

      logger.debug(`Recebidos ${rawCustomers.length} clientes, total: ${totalCount}`);

      return {
        customers: rawCustomers,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    customers: query.data?.customers || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['crm-customers-paginated', currentTenant?.id] 
      });
    },
  };
}
